import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { InboxItem, InboxMessage } from '@entities/operator';
import { extractErrorMessage, httpClient } from '@shared/api';
import { LetterAvatar } from '@shared/ui/v2';
import '@features/test-chat';
import { Feed, statusText, time } from '../operators/chatParts';
import '../test-chat/test-chat.css';
import '../operators/operators.css';
import './operator-link.css';

interface LinkView {
  item: InboxItem;
  messages: InboxMessage[];
  me: string;
  can_reply: boolean;
}

const CHANNEL: Record<string, string> = { max: 'MAX', telegram: 'Telegram', widget: 'Сайт' };

/**
 * Серия O, пакет 2: страница оператора по кнопке «Открыть переписку» из MAX-бота.
 * Без входа в кабинет — по подписанной ссылке оператора (7 дней).
 * Список разговоров как в Telegram (решение 30.10) → переписка; «назад» — к списку.
 * Под телефон: на всю высоту, поле ввода 16px (иначе iPhone увеличивает страницу).
 */
export function OperatorLinkPage() {
  const { token = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const sid = params.get('s');
  // «Назад» из разговора: если пришли из списка — шаг назад, иначе — к списку.
  const toList = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else setParams({}, { replace: true });
  };

  const me = useQuery<{ name: string; first: string }>({
    queryKey: ['op-link', token, 'me'],
    queryFn: async () => (await httpClient.get(`/op-link/${token}/me`)).data,
    retry: false,
  });

  // По ссылке сразу открываем «свой» разговор, а «назад» ведёт к списку.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || !me.data || sid) return;
    opened.current = true;
    setParams({ s: me.data.first });
  }, [me.data, sid, setParams]);

  useNoZoomOnTyping();

  if (me.error) {
    return (
      <div className="ol-page ol-center">
        <div className="ol-logo">RICHAT</div>
        <p>{extractErrorMessage(me.error)}</p>
      </div>
    );
  }
  if (!me.data) return <div className="ol-page ol-center ol-muted">Загружаем…</div>;

  return sid ? (
    <Chat token={token} sid={sid} onBack={toList} />
  ) : (
    <List token={token} name={me.data.name} onOpen={(s) => setParams({ s })} />
  );
}

/** iPhone увеличивает страницу при вводе — фиксируем масштаб, пока открыта страница. */
function useNoZoomOnTyping() {
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    if (!meta) return;
    const prev = meta.content;
    meta.content =
      'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, interactive-widget=resizes-content';
    return () => {
      meta.content = prev;
    };
  }, []);
}

function Avatar({ item, size }: { item: InboxItem; size: number }) {
  const [broken, setBroken] = useState(false);
  if (item.avatar && !broken) {
    return (
      <img
        className="ol-ava"
        src={item.avatar}
        alt=""
        width={size}
        height={size}
        onError={() => setBroken(true)}
        referrerPolicy="no-referrer"
      />
    );
  }
  return <LetterAvatar name={item.client} colorKey={item.id} size={size} />;
}

function ChannelChip({ platform }: { platform: string }) {
  return <span className={`ol-chip is-${platform}`}>{CHANNEL[platform] ?? platform}</span>;
}

// --- Список разговоров (как чаты в Telegram) ------------------------------------

function List({
  token,
  name,
  onOpen,
}: {
  token: string;
  name: string;
  onOpen: (sid: string) => void;
}) {
  const { data } = useQuery<InboxItem[]>({
    queryKey: ['op-link', token, 'inbox'],
    queryFn: async () => (await httpClient.get(`/op-link/${token}/inbox`)).data,
    refetchInterval: 3000,
  });
  useEffect(() => {
    document.title = 'Разговоры — RiChat';
  }, []);
  return (
    <div className="ol-page">
      <header className="ol-head ol-list-head">
        <b>Разговоры</b>
        <span>{name}</span>
      </header>
      <div className="ol-list">
        {data && data.length === 0 && <div className="ol-muted ol-empty">Запросов пока нет.</div>}
        {data?.map((i) => (
          <button
            key={i.id}
            type="button"
            className={`ol-row${i.status === 'closed' ? ' is-closed' : ''}`}
            onClick={() => onOpen(i.id)}
          >
            <Avatar item={i} size={50} />
            <span className="ol-row-body">
              <span className="ol-row-top">
                <b>{i.client}</b>
                <ChannelChip platform={i.platform} />
                <span className="ol-row-time">{time(i.last_at)}</span>
              </span>
              <span className="ol-row-bottom">
                <span className="ol-row-text">
                  {i.status === 'waiting' ? '🔔 ' : ''}
                  {i.last_text}
                </span>
                {i.unread > 0 && <span className="ol-unread">{i.unread}</span>}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Переписка ----------------------------------------------------------------

function Chat({ token, sid, onBack }: { token: string; sid: string; onBack: () => void }) {
  const qc = useQueryClient();
  const key = ['op-link', token, 's', sid];
  const base = `/op-link/${token}/s/${sid}`;
  const { data, error } = useQuery<LinkView>({
    queryKey: key,
    queryFn: async () => (await httpClient.get<LinkView>(base)).data,
    refetchInterval: 2500,
    retry: false,
  });
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const count = data?.messages.length ?? 0;

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [count]);
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);
  useEffect(() => {
    if (data) document.title = `${data.item.client} — RiChat`;
  }, [data]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: key });
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const send = () => {
    const t = text.trim();
    if (!t) return;
    void act(async () => {
      await httpClient.post(`${base}/message`, { text: t });
      setText('');
    });
  };

  const back = (
    <button type="button" className="ol-back" onClick={onBack} aria-label="К списку разговоров">
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M15 18l-6-6 6-6" />
      </svg>
    </button>
  );

  if (error) {
    return (
      <div className="ol-page">
        <header className="ol-head">{back}</header>
        <div className="ol-center ol-muted">{extractErrorMessage(error)}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="ol-page">
        <header className="ol-head">{back}</header>
        <div className="ol-center ol-muted">Загружаем переписку…</div>
      </div>
    );
  }

  const { item } = data;
  const closed = item.status === 'closed';

  return (
    <div className="ol-page">
      <header className="ol-head">
        {back}
        <Avatar item={item} size={36} />
        <div className="ol-who">
          <b>{item.client}</b>
          <span>
            <ChannelChip platform={item.platform} /> #{item.short} · {statusText(item)}
          </span>
        </div>
        {item.status === 'active' && data.can_reply && (
          <button
            type="button"
            className="tc-btn"
            disabled={busy}
            onClick={() => act(() => httpClient.post(`${base}/close`))}
          >
            Завершить
          </button>
        )}
      </header>

      <div className="tc-scroll ol-scroll">
        <div className="tc-feed">
          <Feed messages={data.messages} item={item} />
          <div ref={bottom} />
        </div>
      </div>

      {err && <div className="ol-err">{err}</div>}

      {item.status === 'waiting' ? (
        <div className="ol-accept-zone">
          <button
            type="button"
            className="ol-accept"
            disabled={busy}
            onClick={() => act(() => httpClient.post(`${base}/accept`))}
          >
            Принять разговор
          </button>
        </div>
      ) : closed ? (
        <div className="op-foot-note">Разговор завершён — дальше клиенту отвечает ассистент.</div>
      ) : !data.can_reply ? (
        <div className="op-foot-note">Разговор ведёт {item.operator}.</div>
      ) : (
        <div className="tc-input-zone ol-input-zone">
          <div className="tc-input-wrap">
            <div className="tc-input op-input-line">
              <label htmlFor="ol-msg" className="v2-sr">
                Ответ клиенту
              </label>
              <textarea
                id="ol-msg"
                ref={inputRef}
                rows={1}
                value={text}
                placeholder={`Ответ — клиент увидит «${data.me}»`}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <button
                type="button"
                className="tc-send"
                aria-label="Отправить"
                onClick={send}
                disabled={busy || !text.trim()}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
